import { IconButton, Link, useTheme } from "@mui/material";

import {
  BCPClipboardList,
  BCPFill,
  BCPHandshake,
  BCPList,
  BCPRankingStar,
  BCPCalendarDay,
  BCPRotateLeft,
  BCPUserGroup,
  BCPChartMixed,
  BCPIdBadge,
  BCPUsers,
  BCPTicket,
  BCPUsersViewfinder,
  BCPCodeBranch,
  BCPBrackets,
  BCPSliders,
  BCPLockKeyhole,
  BCPCircleInfo,
  BCPLinkSimple,
  BCPCertificate
} from "../../../components/icons/bcp/BCPIcons";
import DeckIcon from "../../../components/icons/DeckIcon";

import {
  findGetParameter,
  getData,
  sortedPlayersArray
} from "../../../components/utilities/UtilityFunctions";

import { postNotification } from "../../../components/utilities/Notifications";
import moment from "moment";
import "moment-timezone";
import { getEnvironmentVariable } from "../../../global/environmentVariables";
import {
  getLocalStorage,
  removeLocalStorage
} from "../../../components/layout/cookies/CookieFunctions";
import { handlePagingAll } from "../../../components/api/APIUtilities";
const NO_SUBSCRIPTIONS = getEnvironmentVariable("NO_SUBSCRIPTIONS") === "true";
const IS_WARMACHINE = getEnvironmentVariable("COMPANY_FLAG") === "wm";

export function normalizeEventV2Response(v2Event) {
  if (!v2Event) return v2Event;

  const normalized = { ...v2Event };

  normalized.eventId = normalized.id ?? normalized.eventId;
  normalized.eventDate = normalized.dates?.start ?? normalized.eventDate;
  normalized.eventEndDate = normalized.dates?.end ?? normalized.eventEndDate;
  normalized.checkedInPlayers =
    normalized.playerCounts?.checkedIn ?? normalized.checkedInPlayers;
  normalized.notCheckedInPlayers =
    normalized.playerCounts?.notCheckedIn ?? normalized.notCheckedInPlayers;
  normalized.droppedPlayers =
    normalized.playerCounts?.dropped ?? normalized.droppedPlayers;
  normalized.totalPlayers =
    normalized.playerCounts?.total ?? normalized.totalPlayers;
  normalized.queryNumPlayers =
    normalized.playerCounts?.total ?? normalized.queryNumPlayers;
  normalized.currentRound =
    normalized.status?.currentRound ?? normalized.currentRound;
  normalized.numberOfRounds =
    normalized.status?.numberOfRounds ?? normalized.numberOfRounds;
  normalized.started = normalized.status?.started ?? normalized.started;
  normalized.ended = normalized.status?.ended ?? normalized.ended;

  if (normalized.dates?.start) {
    normalized.eventDateDisplay = null;
  }
  if (normalized.dates?.end) {
    normalized.eventEndDateDisplay = null;
  }

  if (normalized.location) {
    normalized.city = normalized.location.city;
    normalized.state = normalized.location.state;
    normalized.country = normalized.location.country;
    normalized.timeZone = normalized.location.timezone;
    normalized.locationName = normalized.location.name;
    normalized.locationId = normalized.location.id;
    normalized.location_Id = normalized.location.id;
    if (normalized.location.point) {
      normalized.coordinate = [
        normalized.location.point.longitude,
        normalized.location.point.latitude
      ];
      normalized.coordinate_point = {
        type: "Point",
        coordinates: [
          normalized.location.point.longitude,
          normalized.location.point.latitude
        ]
      };
    }
  }

  if (normalized.gameSystem) {
    normalized.gameSystemId = normalized.gameSystem.id;
    normalized.gameSystem_Id = normalized.gameSystem.id;
    normalized.gameSystemName = normalized.gameSystem.name;
    normalized.gameSystemManufacturer = normalized.gameSystem.manufacturer;
  }

  if (normalized.gameStore) {
    normalized.gameStoreId = normalized.gameStore.id;
    normalized.gameStore_Id = normalized.gameStore.id;
    normalized.gameStoreName = normalized.gameStore.name;
  }

  if (normalized.owner) {
    normalized.ownerId = normalized.owner.id;
    normalized.owner_Id = normalized.owner.id;
    normalized.ownerFirstName = normalized.owner.firstName;
    normalized.ownerLastName = normalized.owner.lastName;
  }

  if (normalized.flags) {
    normalized.hideLists = normalized.flags.hideLists;
    normalized.hidePlacings = normalized.flags.hidePlacings;
    normalized.hideRoster = normalized.flags.hideRoster;
    normalized.hidePlayerCount = normalized.flags.hidePlayerCount;
    normalized.rankedTables = normalized.flags.rankedTables;
    normalized.enablePasswords = normalized.flags.enablePasswords;
    normalized.passwordlessScoring = normalized.flags.passwordlessScoring;
    normalized.disableCheckin = normalized.flags.disableCheckin;
    normalized.listsLocked = normalized.flags.listsLocked;
    normalized.listSubmissionLocked = normalized.flags.listSubmissionLocked;
    normalized.listsAtCheckin = normalized.flags.listsAtCheckin;
    normalized.requirePairingPublish = normalized.flags.requirePairingPublish;
    normalized.displayOverall = normalized.flags.displayOverall;
  }

  if (normalized.format) {
    normalized.teamEvent = normalized.format.teamEvent;
    normalized.doublesEvent = normalized.format.doublesEvent;
    normalized.leagueEvent = normalized.format.leagueEvent;
    normalized.boardGameEvent = normalized.format.boardGameEvent;
    normalized.pairingStyle = normalized.format.pairingStyle;
    normalized.startingTable = normalized.format.startingTable;
    normalized.defaultRoundLength = normalized.format.defaultRoundLength;
    normalized.bracketPairings = normalized.format.bracketPairings;
    normalized.eventSubType = normalized.format.subType;
    normalized.eventFormat = normalized.format.eventFormat;
  }

  if (normalized.eventImage?.photoUrl) {
    normalized.photoUrl = normalized.eventImage.photoUrl;
  }

  if (normalized.ticketing) {
    normalized.numTickets = normalized.ticketing.numTickets;
    normalized.ticketPrice = normalized.ticketing.price;
    normalized.ticketCurrency = normalized.ticketing.currency;
    normalized.refundDate = normalized.ticketing.refundDate;
    normalized.useTicketing = normalized.ticketing.useTicketing;
    normalized.usingOnlineReg = normalized.ticketing.useTicketing;
    normalized.privateEvent = normalized.ticketing.privateEvent;
    normalized.chargeType = normalized.ticketing.chargeType;
    normalized.playerPaysFees = normalized.ticketing.playerPaysFees;
    if (normalized.ticketing.currency) {
      normalized.pricingDict = {
        [normalized.ticketing.currency]: normalized.ticketing.price || 0
      };
      normalized.availableCurrencies = [normalized.ticketing.currency];
    }
  }

  if (normalized.scorecard) {
    normalized.scorecardId = normalized.scorecard.id;
  }

  if (normalized.teamScorecard) {
    normalized.teamScorecardId = normalized.teamScorecard.id;
  }

  if (normalized.teamEventOptions) {
    normalized.marginOfVictoryForWin =
      normalized.teamEventOptions.marginOfVictoryForWin;
    normalized.marginOfVictoryForTie =
      normalized.teamEventOptions.marginOfVictoryForTie;
    normalized.matchWinsForTie = normalized.teamEventOptions.matchWinsForTie;
    normalized.gamePointsForWin = normalized.teamEventOptions.gamePointsForWin;
  }

  if (normalized.metrics) {
    normalized.placingMetrics = normalized.metrics.placings;
    normalized.pairingMetrics = normalized.metrics.pairings;
    normalized.playerPlacingMetrics = normalized.metrics.playerPlacings;
  }

  normalized.userFollowing = normalized.following ?? normalized.userFollowing;

  if (normalized.description) {
    normalized.eventDescriptionMarkup = normalized.description;
  }

  return normalized;
}

export async function getEvent(eventId, authContext, options) {
  const fetchEvent = async () => {
    if (!eventId) return null;

    var returnObj;
    try {
      returnObj = await getData(
        `/events/${eventId}`,
        {
          role: true
        },
        "v2"
      );

      if (returnObj) {
        returnObj = normalizeEventV2Response(returnObj);
      }

      const isOwner =
        returnObj.ownerId === authContext?.authState?.identity?.userId;
      const isAdmin = authContext?.authState?.identity?.isAdmin === true;
      const isAdminGameSystem =
        authContext?.authState?.identity?.adminGameSystems?.includes(
          returnObj.gameSystemId
        );

      if (!returnObj?.userRole && !isAdmin && !isAdminGameSystem) {
        const roleObj = await getData(`/events/${eventId}/role`, {
          queryStringParameters: {
            userId: authContext?.authState?.identity?.userId
          }
        });
        returnObj.userRole = roleObj;
      }

      try {
        const playerObj = await getData(`/events/${eventId}/currentPlayer`);
        if (playerObj?.id) {
          returnObj.player = playerObj;
        }
      } catch {}

      if (returnObj?.format?.teamEvent) {
        try {
          const teamPlayerObj = await getData(
            `/events/${eventId}/currentTeamPlayer`
          );
          if (teamPlayerObj?.id) {
            returnObj.teamPlayer = teamPlayerObj;
          }
        } catch {}
      }

      if (options?.photo && returnObj.photoFileId) {
        returnObj.photoFile = await fetchFile(returnObj.photoFileId);
      }

      if (options?.circuits || options?.league) {
        returnObj.circuits = await fetchLeagues(
          returnObj?.gameSystemId || returnObj?.gameSystem?.id
        );
      }
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  const fetchFile = async (fileId) => {
    if (!fileId) return;

    var returnObj;
    try {
      returnObj = await getData(`/files/${fileId}`, {
        queryStringParameters: {}
      });
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  const fetchLeagues = async (gSystemId) => {
    let queryParams = {
      public: true,
      active: true,
      sortKey: "name",
      gameSystemId: gSystemId
    };

    try {
      const leagueData = await handlePagingAll("/leagues", queryParams);

      if (leagueData) {
        leagueData.sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
        return leagueData;
      } else return [];
    } catch (err) {
      return [];
    }
  };

  var apiGetData = await fetchEvent();

  if (!apiGetData?.displayOverall) apiGetData.displayOverall = false;
  if (apiGetData.externalUrl) {
    if (!/^https?:\/\//i.test(apiGetData.externalUrl)) {
      apiGetData.externalUrl = "http://" + apiGetData.externalUrl;
    }
  }

  if (apiGetData.eventDate) {
    var eventDate = moment(apiGetData.eventDate);
    if (apiGetData.hasOwnProperty("location")) {
      if (apiGetData.location.hasOwnProperty("timezone")) {
        eventDate = eventDate.tz(apiGetData.location.timezone);
      }
    }
    apiGetData.eventDateDisplay = eventDate;
  }
  if (apiGetData.eventEndDate) {
    var eventEndDate = moment(apiGetData.eventEndDate);
    if (apiGetData.hasOwnProperty("location")) {
      if (apiGetData.location.hasOwnProperty("timezone")) {
        eventEndDate = eventEndDate.tz(apiGetData.location.timezone);
      }
    }
    apiGetData.eventEndDateDisplay = eventEndDate;
  }

  if (apiGetData.pricingDict) {
    var keys = Object.keys(apiGetData.pricingDict);
    if (keys?.length > 0) {
      apiGetData.pricingInfo = {
        currency: keys[0],
        amount: (apiGetData.pricingDict[keys[0]] / 100).toFixed(2)
      };
    }
  }

  apiGetData.eventMetrics = [
    { key: "numWins", name: "Wins" },
    { key: "battlePoints", name: "Battle Points" }
  ];
  if (!apiGetData.placingMetrics) {
    apiGetData.placingMetrics = [
      {
        isOn: true,
        key: "numWins",
        name: "Wins",
        negative: false
      },
      {
        isOn: true,
        key: "battlePoints",
        name: "Battle Points",
        negative: false
      }
    ];
  }
  var x = 0;
  for (var i = 0; i < apiGetData.placingMetrics?.length; i++) {
    if (apiGetData.placingMetrics[i].isOn) {
      apiGetData.eventMetrics[x] = apiGetData.placingMetrics[i];
      x++;
    }
  }

  return apiGetData;
}

export const officialTemplateType = {
  label: "Official",
  sort: 1
};
export const otherTemplateType = {
  label: "Other",
  sort: 999999
};

export async function getEventTemplates(gameSystemId, options) {
  if (!gameSystemId) return null;

  let apiData; // To store the raw API response
  let eventTemplates = []; // Initialize with an empty array

  let queryStringParameters = {
    gameSystemId: gameSystemId,
    limit: 100
  };

  if (options?.limit) {
    queryStringParameters.limit = options.limit;
  }

  if (options?.nextKey) {
    queryStringParameters.nextKey = options.nextKey;
  }

  try {
    apiData = await getData(`/gamesystemtemplates`, queryStringParameters);
    if (apiData && Array.isArray(apiData.data)) {
      for (let template of apiData.data) {
        if (!template.templateType) {
          template.templateType = otherTemplateType;
        }
      }
      eventTemplates = apiData.data;
    }
  } catch (err) {
    console.error("Error in getEventTemplates:", err);
  } finally {
    return eventTemplates;
  }
}

export function BuildRecordJSX({ player }) {
  const theme = useTheme();
  if (player?.games?.length || player?.pod_games?.length) {
    return (
      <>
        {player?.games.map((game, index) => {
          return (
            <>
              {index > 0 && <span> / </span>}
              <span
                style={{
                  color:
                    game.gameResult === 2
                      ? theme?.palette?.win?.main
                      : game.gameResult === 1
                        ? theme?.palette?.draw?.main
                        : theme?.palette?.loss?.main
                }}
              >
                {IS_WARMACHINE
                  ? game.gameResult === 2
                    ? "W"
                    : game.gameResult === 1
                      ? "D"
                      : "L"
                  : game.gamePoints !== undefined
                    ? game.gamePoints
                    : game.gameResult}
              </span>
            </>
          );
        })}
        {player?.pod_games &&
          player.pod_games.map((game, index) => {
            return (
              <>
                {(index > 0 || player?.games?.length > 0) && <span> / </span>}
                <span
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    color:
                      game.gameResult === 2
                        ? theme?.palette?.win?.main
                        : game.gameResult === 1
                          ? theme?.palette?.draw?.main
                          : theme?.palette?.loss?.main
                  }}
                >
                  {game.gamePoints !== undefined
                    ? game.gamePoints
                    : game.gameResult}
                </span>
              </>
            );
          })}
      </>
    );
  }
  return <></>;
}

export function handlePlayersChange(event, currentPlayers, options) {
  currentPlayers = sortedPlayersArray(
    currentPlayers,
    event?.eventMetrics,
    options?.showOverall
  );
  const counters = {
    registered: 0,
    checkedin: 0,
    active: 0,
    waiting: 0
  };

  const newPlayerDict = {};
  const newPlayerArray = [];
  let rank = 0;

  if (currentPlayers?.length) {
    currentPlayers.forEach((player, index) => {
      if (player?.isPod) {
        newPlayerArray.push(player);
        return;
      }
      rank++;
      if (!player?.loadedPlacing && player?.placing) {
        player.loadedPlacing = player.placing;
      }
      player.placing = options?.showOverall
        ? rank
        : player?.loadedPlacing || rank;
      if (!player?.paymentPending) counters.registered++;
      if (player?.checkedIn && !player?.paymentPending) counters.checkedin++;
      if (player?.checkedIn && !player?.dropped && !player?.paymentPending)
        counters.active++;
      player.recordJSX = <></>;
      const podTemp =
        event.bracketPairings && player.podNum === 1 && player.pod_resultRecord
          ? "pod_"
          : "";
      if (player?.[podTemp + "games"]) {
        player.recordJSX = <BuildRecordJSX player={player} />;
      }

      newPlayerDict[player.id] = player;
      if (!player?.paymentPending) newPlayerArray.push(player);
    });
  }

  if (!options?.isPlacings) {
    newPlayerArray.sort((a, b) => {
      const nameA =
        event?.format?.teamEvent || event?.format?.doublesEvent
          ? a?.name?.toLowerCase()
          : userDisplayName(
              a?.firstName || a?.user?.firstName,
              a?.lastName || a?.user?.lastName
            ).toLowerCase();
      const nameB =
        event?.format?.teamEvent || event?.format?.doublesEvent
          ? b?.name?.toLowerCase()
          : userDisplayName(
              b?.firstName || b?.user?.firstName,
              b?.lastName || b?.user?.lastName
            ).toLowerCase();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    });
  }

  return {
    newPlayerDict: newPlayerDict,
    newPlayerArray: newPlayerArray,
    counters: counters
  };
}

export function userDisplayName(firstName, lastName) {
  return `${firstName || ""} ${lastName || ""}`.trim();
}

export function getArmyListColor(player) {
  return player &&
    (player.armyListObjectId || player.armyListId || player.listId)
    ? "success"
    : "error";
}

export function armyListActions(player, event, organize) {
  if (!player || !event) return {};
  return {
    onClick:
      event.flags?.hideLists && !organize
        ? (e) => {
            e.stopPropagation();
            postNotification(
              "warning",
              `This Event has ${
                event?.gameSystem?.listLabel || "List"
              }s set to hidden`
            );
          }
        : !player.armyListObjectId && !player.armyListId && !player.listId
          ? (e) => {
              e.stopPropagation();
              postNotification(
                "warning",
                `${userDisplayName(
                  player?.firstName || player?.user?.firstName,
                  player?.lastName || player?.user?.lastName
                )} has not submitted their ${
                  event?.gameSystem?.listLabel || "List"
                }`
              );
            }
          : (e) => {
              e.stopPropagation();
            },
    href: !(!event.flags?.hideLists || organize)
      ? null
      : player.listUrl !== undefined && player.listUrl !== ""
        ? player.listUrl
        : player.armyListSolforge
          ? `${getEnvironmentVariable("HEADER_SOLFORGE_URL") || ""}/fused/${
              player.armyListSolforge.id
            }`
          : player.armyListObjectId || player.armyListId || player.listId
            ? `/list/${player.armyListObjectId || player.armyListId || player.listId}`
            : null,
    target:
      (!event.flags?.hideLists || organize) &&
      (player.armyListObjectId || player.armyListId || player.listId)
        ? `_blank`
        : null
  };
}

export function ArmyListIconButton(props) {
  const { organize, player, event, dense, sx } = props;
  const actions = armyListActions(player, event, organize);
  return (
    <IconButton
      {...actions}
      sx={{
        bgcolor: dense
          ? null
          : event.flags?.hideLists && !organize
            ? "text.disabled"
            : `${getArmyListColor(player)}.main`,
        color: dense
          ? event.flags?.hideLists && !organize
            ? "text.disabled"
            : `${getArmyListColor(player)}.main`
          : event.flags?.hideLists && !organize
            ? "background.secondary"
            : `${getArmyListColor(player)}.contrastText`,
        "&:hover": {
          bgcolor: dense
            ? null
            : event.flags?.hideLists && !organize
              ? "text.disabled"
              : `${getArmyListColor(player)}.dark`,
          color: dense ? `${getArmyListColor(player)}.dark` : null
        },

        ...sx
      }}
    >
      {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
        <DeckIcon color={getArmyListColor(player)} />
      ) : (
        <BCPClipboardList />
      )}
    </IconButton>
  );
}

export function ArmyListLink(props) {
  const { organize, player, event } = props;
  const actions = armyListActions(player, event, organize);
  return (
    <Link
      {...actions}
      underline="none"
      color={
        player?.armyListObjectId || player?.armyListId || player?.listId
          ? "link"
          : "text.secondary"
      }
    >
      {player?.armyListObjectId || player?.armyListId || player?.listId
        ? "View List"
        : "No List"}
    </Link>
  );
}

export function getMenuItems(
  event,
  authContext,
  setResetModalOpen,
  setShareModalOpen,
  appRoute,
  t = null
) {
  if (!event?.id) {
    return null;
  }

  const embed = appRoute || findGetParameter("embed") === "true";

  var middleItems = [
    {
      title: event?.name
    },
    {
      divider: true
    },
    {
      title: t ? t("eventActions") : "Event Actions"
    },
    {
      href: `/organize/event/${event.id}?active_tab=roster${
        embed ? "&embed=true" : ""
      }`,
      icon: BCPUserGroup,
      title: t ? t("playersAndLists") : "Players & Lists"
    },
    {
      href: `/organize/event/${event.id}?active_tab=pairings${
        embed ? "&embed=true" : ""
      }`,
      icon: BCPHandshake,
      title: t ? t("pairings") : "Pairings"
    },

    event?.topCut ||
    event?.format?.pairingStyle === "single elimination" ||
    event?.format?.pairingStyle === "double elimination"
      ? {
          href: `/organize/event/${event.id}?active_tab=brackets${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPBrackets,
          title: t ? t("brackets") : "Brackets"
        }
      : null,

    {
      href: `/organize/event/${event.id}?active_tab=placings${
        embed ? "&embed=true" : ""
      }`,
      icon: BCPRankingStar,
      title: t ? t("leaderboard") : "Leaderboard"
    },
    authContext?.authState?.identity?.isAdmin === true
      ? {
          href: `/organize/event/${event.id}?active_tab=stats${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPChartMixed,
          title: t ? t("stats") : "Stats"
        }
      : null,
    event?.gameSystem?.supportSoftScores
      ? {
          href: `/organize/event/${event.id}?active_tab=soft-scores${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPFill,
          title: t ? t("softHobbyScores") : "Soft/Hobby Scores"
        }
      : null,
    authContext?.authState?.identity?.userId === event?.ownerId ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystemId
    ) ||
    authContext?.authState?.identity?.isJudge ||
    event?.userRole?.permissions?.judgeReport !== undefined
      ? {
          href: `/organize/event/${event.id}?active_tab=judge-report${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPIdBadge,
          title: t ? t("judgeReport") : "Judge Report"
        }
      : null,
    { divider: true, sx: { mt: 1, mb: 1 } },
    {
      title: t ? t("eventSettings") : "Event Settings"
    },
    authContext?.authState?.identity?.userId === event?.ownerId ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    event?.userRole?.permissions?.eventDetails !== undefined
      ? {
          href: `/organize/event/${event.id}?active_tab=info${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPCircleInfo,
          title: t ? t("info") : "Info"
        }
      : null,
    authContext?.authState?.identity?.userId === event?.ownerId ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    event?.userRole?.permissions?.eventDetails !== undefined
      ? {
          href: `/organize/event/${event.id}?active_tab=details${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPCalendarDay,
          title: t ? t("eventDetails") : "Event Details"
        }
      : null,
    authContext?.authState?.identity?.userId === event?.ownerId ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    event?.userRole?.permissions?.eventDetails !== undefined
      ? {
          href: `/organize/event/${event.id}?active_tab=config${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPSliders,
          title: t ? t("gameConfiguration") : "Game Configuration"
        }
      : null,
    authContext?.authState?.identity?.userId === event?.ownerId ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    event?.userRole?.permissions?.eventDetails !== undefined
      ? {
          href: `/organize/event/${event.id}?active_tab=registration${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPTicket,
          title: t ? t("registrationAndTickets") : "Registration & Tickets"
        }
      : null,
    (authContext?.authState?.identity?.userId === event?.ownerId &&
      event?.circuits?.length > 0) ||
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    (event?.userRole?.permissions?.eventDetails !== undefined &&
      event?.circuits?.length > 0)
      ? {
          href: `/organize/event/${event.id}?active_tab=circuits${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPCertificate,
          title: t ? t("circuits") : "Circuits"
        }
      : null,
    authContext?.authState?.identity?.isAdmin === true ||
    authContext?.authState?.identity?.adminGameSystems?.includes(
      event?.gameSystem.id
    ) ||
    NO_SUBSCRIPTIONS !== undefined ||
    (event?.owner?.subscribed &&
      event?.userRole?.permissions?.eventUsers !== undefined)
      ? {
          href: `/organize/event/${event.id}?active_tab=users${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPUserGroup,
          title: t ? t("eventUsers") : "Event Users"
        }
      : null,
    event?.ticketing?.privateEvent
      ? {
          href: `/organize/event/${event.id}?active_tab=access-codes${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPLockKeyhole,
          title: t ? t("accessCodes") : "Access Codes"
        }
      : null,
    { divider: true, sx: { mt: 1, mb: 1 } },
    {
      onClick: () => {
        setResetModalOpen(true);
      },
      icon: BCPRotateLeft,
      title: t ? t("reset") : "Reset"
    },
    {
      onClick: () => {
        setShareModalOpen(true);
      },
      icon: BCPLinkSimple,
      title: t ? t("shareAndEmbed") : "Share & Embed"
    },
    {
      href: `/event/${event.id}?active_tab=overview`,
      icon: BCPUsersViewfinder,
      title: t ? t("publicView") : "Public View"
    }
  ];
  return middleItems;
}

export function getPairingMenuItems(pairing, appRoute, t = null) {
  if (!pairing?.event?.id) {
    return null;
  }

  const embed = appRoute || findGetParameter("embed") === "true";

  var middleItems = [
    {
      title: pairing.event?.name
    },
    pairing?.teamPairingId
      ? {
          href: `/game/teampairing/${pairing.teamPairingId}${
            embed ? "?embed=true" : ""
          }`,
          icon: BCPUsers,
          title: t ? t("teamPairing") : "Team Pairing"
        }
      : null,
    {
      href: `/${pairing.event.isTO ? "organize/" : ""}event/${
        pairing.event.id
      }?active_tab=roster${embed ? "&embed=true" : ""}`,
      icon: BCPList,
      title: t ? t("roster") : "Roster"
    },
    {
      href: `/${pairing.event.isTO ? "organize/" : ""}event/${
        pairing.event.id
      }?active_tab=pairings${embed ? "&embed=true" : ""}`,
      icon: BCPHandshake,
      title: t ? t("pairings") : "Pairings"
    },

    pairing?.event?.format?.pairingStyle === "single elimination" ||
    pairing?.event?.format?.pairingStyle === "double elimination"
      ? {
          href: `/organize/event/${pairing.event.id}?active_tab=brackets${
            embed ? "&embed=true" : ""
          }`,
          icon: BCPCodeBranch,
          title: t ? t("brackets") : "Brackets"
        }
      : null,

    {
      href: `/${pairing.event.isTO ? "organize/" : ""}event/${
        pairing.event.id
      }?active_tab=placings${embed ? "&embed=true" : ""}`,
      icon: BCPRankingStar,
      title: t ? t("placings") : "Placings"
    }
  ];

  return middleItems;
}

export function getSoftScoreMenuItems(event, appRoute, t = null) {
  if (!event?.id) {
    return null;
  }

  const embed = appRoute || findGetParameter("embed") === "true";

  var middleItems = [
    {
      title: event?.name
    },
    {
      href: `/${event.isTO ? "organize/" : ""}event/${
        event.id
      }?active_tab=roster${embed ? "&embed=true" : ""}`,
      icon: BCPList,
      title: t ? t("roster") : "Roster"
    }
  ];
  if (event.status?.numberOfRounds > 0) {
    middleItems.push({
      href: `/${event.isTO ? "organize/" : ""}event/${
        event.id
      }?active_tab=pairings${embed ? "&embed=true" : ""}`,
      icon: BCPHandshake,
      title: t ? t("pairings") : "Pairings"
    });
  }

  if (
    event?.format?.pairingStyle === "single elimination" ||
    event?.format?.pairingStyle === "double elimination"
  ) {
    middleItems.push({
      href: `/organize/event/${event.id}?active_tab=brackets${
        embed ? "&embed=true" : ""
      }`,
      icon: BCPCodeBranch,
      title: "Brackets"
    });
  }

  middleItems.push({
    href: `/${event.isTO ? "organize/" : ""}event/${
      event.id
    }?active_tab=placings${embed ? "&embed=true" : ""}`,
    icon: BCPRankingStar,
    title: "Placings"
  });

  return middleItems;
}

export function refreshRoster() {
  removeLocalStorage("roster");
  removeLocalStorage("rosterData");
  window.location.reload();
}

export function refreshPlacings() {
  removeLocalStorage("placings");
  removeLocalStorage("placingsData");
  window.location.reload();
}

export function getRosterLocalVars() {
  const locRoster = getLocalStorage("roster")
    ? JSON.parse(getLocalStorage("roster"))
    : null;
  const locRosterMetaData = getLocalStorage("rosterData")
    ? JSON.parse(getLocalStorage("rosterData"))
    : null;

  return {
    locRoster,
    locRosterMetaData
  };
}

export function getPlacingsLocalVars() {
  const locPlacings = getLocalStorage("placings")
    ? JSON.parse(getLocalStorage("placings"))
    : null;
  const locPlacingsData = getLocalStorage("placingsData")
    ? JSON.parse(getLocalStorage("placingsData"))
    : null;

  return {
    locPlacings,
    locPlacingsData
  };
}

export function refreshPairings(reload = true) {
  removeLocalStorage("pairings");
  removeLocalStorage("pairingsData");
  if (reload) {
    window.location.reload();
  }
}

export function getPairingsLocalVars() {
  const locPairings = getLocalStorage("pairings")
    ? JSON.parse(getLocalStorage("pairings"))
    : null;
  const locPairingsData = getLocalStorage("pairingsData")
    ? JSON.parse(getLocalStorage("pairingsData"))
    : null;

  return {
    locPairings,
    locPairingsData
  };
}

export function markdownToPlainText(str) {
  // remove headers
  str = str.replace(/#+\s+(.*)/g, "$1");

  // remove images
  str = str.replace(/!\[.*\]\(.*\)/g, "");

  // remove links
  str = str.replace(/\[(.*?)\]\((.*?)\)/g, "");

  // remove bold text
  str = str.replace(/\*\*(.*)\*\*/g, "$1");

  // remove italicized text
  str = str.replace(/\*(.*)\*/g, "$1");

  // remove inline code
  str = str.replace(/`(.*)`/g, "$1");

  // remove horizontal lines
  str = str.replace(/\n---\n/g, "");

  // remove blockquotes
  str = str.replace(/^>\s+/gm, "");

  // remove strikethrough text
  str = str.replace(/~~(.*?)~~/g, "$1");

  // remove backticks
  str = str.replace(/`/g, "");

  return str.trim();
}

export function getWebsocketUrl() {
  const WEBSOCKET_URL = getEnvironmentVariable("WEBSOCKET_URL")
    ? getEnvironmentVariable("WEBSOCKET_URL")
    : getEnvironmentVariable("AMPLIFY_BRANCH") === "testing" ||
        getEnvironmentVariable("AMPLIFY_BRANCH") ===
          "testing-organizations-overhaul"
      ? "wss://test-ws.bestcoastpairings.com"
      : getEnvironmentVariable("AMPLIFY_BRANCH") === "bcp-staging"
        ? "wss://newstaging-ws.bestcoastpairings.com"
        : "wss://newdevelop-ws.bestcoastpairings.com/";

  return WEBSOCKET_URL;
}
